import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  EChartsPlot,
  getSciChartTheme,
  SCI_PALETTE,
  useThemeVersion,
} from "@/lib/science/sci-chart";
import { SciTextArea } from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { gcSkew } from "../lib/gc-skew";

export function GcSkewCalc() {
  const locale = getLocale();
  const themeVersion = useThemeVersion();
  const [seq, setSeq] = useState("");
  const [window, setWindow] = useState("100");

  const r = useMemo(() => {
    const w = Math.max(1, Math.round(Number(window) || 100));
    return gcSkew(seq, w);
  }, [seq, window]);

  const option = useMemo(() => {
    const theme = getSciChartTheme();
    const axis = {
      axisLabel: { color: theme.muted, fontSize: 10 },
      axisLine: { lineStyle: { color: theme.border } },
      splitLine: { lineStyle: { color: theme.border, opacity: 0.3 } },
    };
    return {
      tooltip: { trigger: "axis" },
      legend: {
        data: [m.gcskew_series_skew(), m.gcskew_series_cum()],
        bottom: 0,
        textStyle: { color: theme.muted },
      },
      grid: { left: 48, right: 20, top: 20, bottom: 44 },
      xAxis: {
        type: "category",
        data: r.positions,
        name: m.gcskew_x_axis(),
        nameLocation: "middle",
        nameGap: 26,
        nameTextStyle: { color: theme.muted },
        ...axis,
      },
      yAxis: { type: "value", ...axis },
      series: [
        {
          name: m.gcskew_series_skew(),
          type: "line",
          data: r.skew,
          smooth: true,
          showSymbol: false,
          color: SCI_PALETTE[0],
          lineStyle: { width: 2 },
        },
        {
          name: m.gcskew_series_cum(),
          type: "line",
          data: r.cumulative,
          smooth: true,
          showSymbol: false,
          color: SCI_PALETTE[1],
          lineStyle: { width: 2 },
          areaStyle: { color: SCI_PALETTE[1], opacity: 0.12 },
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r, themeVersion]);

  return (
    <div className="sci-card rounded-2xl p-6 sm:p-8">
      <SciTextArea
        id="gcskew-input"
        label={m.gcskew_input_label()}
        value={seq}
        placeholder={m.gcskew_input_placeholder()}
        onChange={setSeq}
      />

      <div className="mt-4 flex items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="gcskew-window" className="text-muted-foreground">
            {m.gcskew_window_label()}
          </Label>
          <Input
            id="gcskew-window"
            type="number"
            min={1}
            value={window}
            onChange={(e) => setWindow(e.target.value)}
            className="w-32 border-black/15 bg-white text-neutral-900 hover:border-black/25 dark:border-black/15 dark:bg-white dark:text-neutral-900"
          />
        </div>
      </div>

      {r.windows > 0 ? (
        <>
          <div className="sci-result-zone mt-5 flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
            <span className="text-sm font-medium text-muted-foreground">
              {m.gcskew_overall()}
            </span>
            <span
              data-testid="gcskew-overall"
              className="sci-result font-display text-3xl font-bold sm:text-4xl"
            >
              {r.overall.toLocaleString(locale, { maximumFractionDigits: 3 })}
            </span>
          </div>
          <div className="mt-5">
            <EChartsPlot option={option} height={360} testId="gcskew-chart" />
          </div>
        </>
      ) : (
        <p className="mt-5 text-sm text-muted-foreground">
          {m.gcskew_hint_empty()}
        </p>
      )}
    </div>
  );
}
